// CR075-P5: Feature Flags Display for Batch Runs
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ShieldAlert, ShieldCheck, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface FeatureFlag {
  flag_name: string;
  enabled: boolean;
  description: string | null;
}

const BATCH_RUN_FLAGS = [
  "ff_batch_runs_publish_enabled",
  "ff_batch_runs_clear_staging_enabled",
  "ff_batch_runs_purge_enabled",
  "ff_batch_runs_dry_run_enabled",
];

export function BatchRunFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFlags = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("flag_name, enabled, description")
        .in("flag_name", BATCH_RUN_FLAGS);

      if (error) throw error;
      setFlags((data || []) as FeatureFlag[]);
    } catch (err) {
      console.error("Failed to fetch feature flags:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlags();
  }, []);

  const getShortName = (flagName: string): string => {
    return flagName
      .replace("ff_batch_runs_", "")
      .replace(/_/g, " ")
      .replace("enabled", "")
      .trim();
  };

  if (loading) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Feature Flags:</span>
        {flags.map((flag) => (
          <Tooltip key={flag.flag_name}>
            <TooltipTrigger asChild>
              <Badge
                variant={flag.enabled ? "default" : "secondary"}
                className="gap-1 cursor-help"
              >
                {flag.enabled ? (
                  <ShieldCheck className="h-3 w-3" />
                ) : (
                  <ShieldAlert className="h-3 w-3" />
                )}
                {getShortName(flag.flag_name)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">{flag.flag_name}</p>
              <p className="text-xs">{flag.description}</p>
              <p className="text-xs mt-1">Status: {flag.enabled ? "Enabled" : "Disabled"}</p>
            </TooltipContent>
          </Tooltip>
        ))}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={fetchFlags}
        >
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>
    </TooltipProvider>
  );
}
